import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Printer, X, Loader2 } from "lucide-react";
import PaymentDocumentPaper from "./PaymentDocumentPaper";
import { getPaymentDetail } from "../../../api/finance/payment/paymentRequests";
import { showErrorToast } from "../../../components/ConfirmDialog";

const PaymentPrintModal = ({ isOpen, onClose, paymentId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !paymentId) {
      setData(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const res = await getPaymentDetail(paymentId);
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) {
          showErrorToast(
            err?.response?.data?.message || "Không tải được phiếu để in."
          );
          onClose?.();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, paymentId]);

  if (!isOpen) return null;

  const lines = Array.isArray(data?.lines) ? data.lines : [];
  const code = data?.request_code || data?.payment_code;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] bg-gray-900/60 backdrop-blur-sm no-print"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[160] flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="w-full max-w-3xl max-h-[92vh] rounded-3xl bg-white overflow-hidden shadow-2xl pointer-events-auto flex flex-col">
              <style>{`
                @media print {
                  .no-print { display: none !important; }
                  body { background: white !important; }
                  @page { margin: 12mm; }
                  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                }
              `}</style>

              <div className="px-6 py-4 bg-slate-900 flex items-center justify-between no-print shrink-0">
                <div className="text-white min-w-0">
                  <p className="text-xs uppercase tracking-wider font-bold opacity-70">
                    Tải phiếu thanh toán
                  </p>
                  <h3 className="text-sm font-black mt-1 truncate">{code || "—"}</h3>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={loading || !data}
                    onClick={() => window.print()}
                    className="h-10 px-4 rounded-xl bg-white/10 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
                  >
                    <Printer size={14} />
                    In / Tải PDF
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="h-10 w-10 rounded-xl bg-white/10 text-white"
                  >
                    <X size={16} className="mx-auto" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-slate-100/80">
                {loading ? (
                  <div className="flex items-center justify-center py-16 text-slate-500 gap-2">
                    <Loader2 size={22} className="animate-spin text-red-600" />
                    Đang tải phiếu...
                  </div>
                ) : data ? (
                  <div className="mx-auto w-full max-w-[640px]">
                    <PaymentDocumentPaper data={data} lines={lines} />
                  </div>
                ) : null}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PaymentPrintModal;
